'use client';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useAsync } from 'react-use';
import { useFlags } from '../../context/FlagsContext';
import { UppyContext } from '../../hooks/useUppy';
import type { CompanionOptions } from '../../hooks/useUppy/getUppyInstance';
import { getBasePath } from './BasePath/getBasePath';

const getUppyLib = async () => {
  const getUppyInstance = await import('../../hooks/useUppy/getUppyInstance');

  return { getUppyInstance };
};

export const useUppyLazyGetInstance = () => {
  const { value, loading } = useAsync(getUppyLib, []);

  return { getUppyInstance: value?.getUppyInstance.default, isReady: !loading };
};

export default function UppyProvider({
  disabledMultipartUpload,
  queryMeta,
  children,
}: PropsWithChildren<CompanionOptions>) {
  const { getUppyInstance, isReady } = useUppyLazyGetInstance();
  const { isPublicUploadAllowed } = useFlags();

  // reinitialize uppy on user refetch
  const uppyInstance = useMemo(() => {
    const baseUrl: string = getBasePath(queryMeta);
    return isReady
      ? {
          loading: false,
          Uppy:
            getUppyInstance?.(`${baseUrl}/api/uploads`, {
              disabledMultipartUpload,
              queryMeta,
              isPublicUploadAllowed,
            }) ?? null,
        }
      : { loading: true, Uppy: null };
  }, [disabledMultipartUpload, getUppyInstance, isReady, queryMeta, isPublicUploadAllowed]);

  return <UppyContext.Provider value={uppyInstance}>{children}</UppyContext.Provider>;
}
